// API Response type (matches backend)
export interface MissedCallApiResponse {
  id: number
  company: { id: number; name: string }
  pbx: { id: number; name: string }
  status: string
  type: string
  direction: 'inbound' | 'outbound'
  from: {
    caller_id?: string
    formatted: string
    cnam_location?: string
    extension?: any
    phone_number?: any
    location?: any
    user?: any
  }
  to: {
    formatted: string
    cnam_location?: string
    extension?: any
    phone_number?: any
    location?: any
    user?: any
  }
  is_missed: boolean
  duration_in_seconds: number
  started_at: string
  externally_transferred_at: string | null
  voicemail_id: number | null
  fax_id: number | null
  transcript_id: number | null
  transcription_status: string | null
  call_sentiment: {
    customer_overall_score?: number
    office_overall_score?: number
  } | null
  ended_at: string | null
  created_at: string
  updated_at: string
  missed_type: string | null
  missed_call_return_status: string | null
  missed_call_tracking?: {
    follow_up_status: string
    follow_up_user: number | null
    follow_up_time_utc: string | null
    created_by: number
    inserted_at: string
  }
}

// UI-friendly type (what components use)
export interface MissedCall {
  id: number
  created_at: string
  to_number: string
  from_number: string
  type: string
  follow_up_status: string
  assigned_to_user_id?: number | null
  from_caller_id?: string
  to_caller_id?: string
  voicemail_id?: number | null
  duration_in_seconds?: number
  follow_up_time_utc?: string | null
}

// Map API status values to UI dropdown values
const mapApiStatusToUiStatus = (
  apiStatus: string | null | undefined,
): string => {
  if (!apiStatus) return 'Open'

  const statusMap: Record<string, string> = {
    open: 'Open',
    returned_callback: 'Returned - Call back',
    returned_call_back: 'Returned - Call back',
    returned_sms: 'Returned - SMS',
    returned_voicemail: 'Returned - Voicemail',
    returned_email: 'Returned - Email',
    returned_no_answer: 'Returned - No answer',
    no_follow_up_required: 'No follow up required',
  }

  // Convert to lowercase for case-insensitive matching
  const normalizedStatus = apiStatus.toLowerCase().trim()
  return statusMap[normalizedStatus] || 'Open'
}

// Map UI dropdown values back to API status values
export const mapUiStatusToApiStatus = (uiStatus: string): string => {
  const statusMap: Record<string, string> = {
    Open: 'open',
    'Returned - Call back': 'returned_callback',
    'Returned - SMS': 'returned_sms',
    'Returned - Voicemail': 'returned_voicemail',
    'Returned - Email': 'returned_email',
    'Returned - No answer': 'returned_no_answer',
    'No follow up required': 'no_follow_up_required',
  }

  return statusMap[uiStatus] || 'open'
}

// Transform API response to UI type
export const transformMissedCallApiResponse = (
  apiResponse: MissedCallApiResponse,
): MissedCall => {
  const apiStatus =
    apiResponse.missed_call_tracking?.follow_up_status ||
    apiResponse.missed_call_return_status

  return {
    id: apiResponse.id,
    created_at: apiResponse.started_at || apiResponse.created_at,
    to_number: apiResponse.to.formatted,
    from_number: apiResponse.from.formatted,
    type: apiResponse.voicemail_id ? 'voicemail' : 'abandoned',
    follow_up_status: mapApiStatusToUiStatus(apiStatus),
    assigned_to_user_id:
      apiResponse.missed_call_tracking?.follow_up_user ?? null,
    from_caller_id:
      apiResponse.from.caller_id || apiResponse.from.cnam_location,
    to_caller_id:
      apiResponse.to.phone_number?.name || apiResponse.to.cnam_location,
    voicemail_id: apiResponse.voicemail_id,
    duration_in_seconds: apiResponse.duration_in_seconds,
    follow_up_time_utc:
      apiResponse.missed_call_tracking?.follow_up_time_utc ?? null,
  }
}
